import { Module } from "vuex";
import { RootState } from "../RootState";
import { AutonomousFormBaseCheckbox } from "../../models/responses/AutonomousFormBaseCheckbox";
import { FlowBaseCheckboxUpdateRequest } from "../../models/requests/FlowBaseCheckboxUpdateRequest";
import { AutonomousFormLegalPersonBaseCheckboxService } from "../../services/AutonomousFormLegalPersonBaseCheckboxService";
import { CheckboxTypes } from "../../models/CheckboxTypes";

export type FlowCheckboxKeyValue = {
  name: string;
  checkbox: AutonomousFormBaseCheckbox;
};

export interface AutonomousFormBaseCheckboxState {
  autonomousFormLegalPersonBaseCheckboxes: FlowCheckboxKeyValue[];
}

async function getCheckboxWithName(
  name: string,
  flowId: number
): Promise<FlowCheckboxKeyValue> {
  const response = await AutonomousFormLegalPersonBaseCheckboxService.getCheckbox(
    name,
    flowId
  );
  return { name: name, checkbox: response.data };
}

// Magasin VueX qui coordonne l'échange d'infos entre les différentes vues.
export const autonomousFormLegalPersonBaseCheckboxModule: Module<
  AutonomousFormBaseCheckboxState,
  RootState
> = {
  namespaced: true,
  // Données partagées du magasin (ne peuvent pas être modifiées directement)
  state: {
    autonomousFormLegalPersonBaseCheckboxes: []
  },

  // Accesseurs pour propriétés calculées
  getters: {},

  // Opérations de modification des données
  mutations: {
    /**
     * Updates autonomousFormBaseCheckboxes
     */
    setAutonomousFormLegalPersonBaseCheckboxes(
      state,
      value: FlowCheckboxKeyValue[]
    ) {
      state.autonomousFormLegalPersonBaseCheckboxes = value;
    },

    /**
     * Updates one base Checkbox by name
     * @param state
     * @param value
     */
    setAutonomousFormLegalPersonBaseCheckbox(
      state,
      value: { name: string; checkbox: AutonomousFormBaseCheckbox }
    ) {
      const index = state.autonomousFormLegalPersonBaseCheckboxes.findIndex(
        val => val.name === value.name
      );
      const keyValue: FlowCheckboxKeyValue = {
        name: value.name,
        checkbox: value.checkbox
      };
      if (index > -1) {
        state.autonomousFormLegalPersonBaseCheckboxes.splice(
          index,
          1,
          keyValue
        );
      }
    }
  },

  // Actions pouvant être appelées par les vues pour agir sur le magasin
  actions: {
    async loadCheckbox({ commit }, value: { name: string; flowId: number }) {
      const response = await AutonomousFormLegalPersonBaseCheckboxService.getCheckbox(
        value.name,
        value.flowId
      );
      commit("setAutonomousFormLegalPersonBaseCheckbox", {
        name: value.name,
        checkbox: response.data
      });
      return response.data;
    },

    async clearCheckbox(
      { commit },
      value: {
        name: string;
        flowId: number;
        checkbox: FlowBaseCheckboxUpdateRequest;
      }
    ) {
      const getResponse = await AutonomousFormLegalPersonBaseCheckboxService.getCheckbox(
        value.name,
        value.flowId
      );
      commit("setAutonomousFormLegalPersonBaseCheckbox", {
        name: value.name,
        checkbox: getResponse.data
      });
      return getResponse.data;
    },

    async setCheckbox(
      context,
      value: {
        name: string;
        flowId: number;
        checkbox: FlowBaseCheckboxUpdateRequest;
      }
    ) {
      const response = await AutonomousFormLegalPersonBaseCheckboxService.setCheckbox(
        value.name,
        value.flowId,
        value.checkbox
      );
      context.commit("setAutonomousFormLegalPersonBaseCheckbox", {
        name: value.name,
        checkbox: response.data
      });
      return response.data;
    },

    async loadAllCheckbox(context, flowId: number) {
      const results = await Promise.all(
        CheckboxTypes.map(val => getCheckboxWithName(val.type, flowId))
      );

      context.commit("setAutonomousFormLegalPersonBaseCheckboxes", results);
      return results;
    }
  }
};
